import type { RyftAccountGetResponseType } from "@sellar-io/api-v2/types";
import { AlertContainer } from "~/components/alert/AlertContainer";
import { Icon } from "~/components";
import type { IconType } from "@sellar-io/components";
import { twMerge } from "tailwind-merge";
import type { AlertType } from "~/components/alert/AlertType";
import { Link } from "react-router";
import {
  BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ACCOUNTS,
  BUSINESS_INTEGRATION_RYFT_NON_HOSTED_DOCUMENTS,
} from "~/constants/routes";
import {
  getRyftBusinessErrors,
  getRyftVerificationStatuses,
  type RyftBusinessError,
} from "./RyftNonHostedDashboard.shared";

type VerificationStatuses = ReturnType<typeof getRyftVerificationStatuses>;
type VerificationIndicator = {
  title: string;
  type: AlertType;
  description?: string;
  icon: IconType;
  className: string;
};

const getVerificationIndicator = (
  statuses: VerificationStatuses,
  hasErrors: boolean,
): VerificationIndicator => {
  const values = [statuses.business, statuses.director];
  if (hasErrors || values.includes("Required")) {
    return {
      title: "Attention required",
      description: "Review your account information or documents.",
      type: "warning",
      icon: "ALERT",
      className: "text-orange-800",
    };
  }
  if (values.includes("PendingVerification")) {
    return {
      title: "Verification in progress",
      description: "Ryft is reviewing your details and documents.",
      type: "information",
      icon: "CLOCK_OUTLINE",
      className: "text-blue-800",
    };
  }
  if (values.includes("NotRequired")) {
    return {
      title: "Verification not required",
      description: "No further verification is needed right now.",
      type: "success",
      icon: "CHECK_CIRCLE",
      className: "text-emerald-800",
    };
  }
  return {
    title: "Verified",
    description: "Ryft has verified your details and documents.",
    type: "success",
    icon: "CHECK_CIRCLE",
    className: "text-emerald-800",
  };
};

const canReviewError = (
  error: RyftBusinessError,
  statuses: VerificationStatuses,
) => {
  if (error.type === "director-documents") {
    return statuses.director !== "PendingVerification";
  }
  if (error.type === "business-documents") {
    return statuses.business !== "PendingVerification";
  }
  return true;
};

export const RyftNonHostedVerificationStatus = ({
  account,
}: {
  account: RyftAccountGetResponseType;
}) => {
  const { errors } = getRyftBusinessErrors(account);
  const statuses = getRyftVerificationStatuses(account);

  if (
    statuses.business === "Verified" &&
    statuses.director === "Verified" &&
    errors.length === 0
  ) {
    return null;
  }

  const availableErrors = errors.filter((error) =>
    canReviewError(error, statuses),
  );

  const reviewAccount = availableErrors.some(
    (error) =>
      error.type === "business-details" ||
      error.type === "director-details" ||
      error.type === "bank-account",
  );

  const reviewDocuments = availableErrors.some(
    (error) =>
      error.type === "business-documents" ||
      error.type === "director-documents",
  );

  const indicator = getVerificationIndicator(statuses, errors.length > 0);

  return (
    <div className="mx-4 md:mx-6">
      <AlertContainer
        role="status"
        type={indicator.type}
        className={twMerge(
          "flex w-full flex-col justify-between gap-2 px-3 py-2 text-sm sm:flex-row sm:items-center",
          indicator.className,
        )}
      >
        <div className="flex items-center gap-2">
          <Icon icon={indicator.icon} />
          <p>
            <span className="font-semibold">{indicator.title}</span>
            {indicator.description && (
              <>
                {" "}
                &ndash; <span>{indicator.description}</span>
              </>
            )}
          </p>
        </div>
        {(reviewAccount || reviewDocuments) && (
          <div className="flex shrink-0 items-center gap-2">
            {reviewAccount && (
              <Link
                to={BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ACCOUNTS}
                className="cursor-pointer font-bold text-nowrap underline underline-offset-2"
              >
                Review account
              </Link>
            )}
            {reviewAccount && reviewDocuments && (
              <span aria-hidden="true" className="text-orange-400">
                |
              </span>
            )}
            {reviewDocuments && (
              <Link
                to={BUSINESS_INTEGRATION_RYFT_NON_HOSTED_DOCUMENTS}
                className="cursor-pointer font-semibold text-nowrap underline underline-offset-2"
              >
                Review documents
              </Link>
            )}
          </div>
        )}
      </AlertContainer>
    </div>
  );
};
